import type { ResolvedRow } from '@hyphengroup/cms-components/components/ProductDetails/utils/resolveAttributeRow/resolveAttributeRow';
import classNames from 'classnames';
import { FC, useId } from 'react';

/** A resolved list attribute, plus the text colour of its chips (each row has its own in the design). */
export type FlexibleHeroBlockStkBkaChipRow = Extract<
  ResolvedRow,
  { kind: 'list' }
> & {
  chipTextClassName: string;
};

type FlexibleHeroBlockStkBkaAttributeChipsProps = {
  rows: FlexibleHeroBlockStkBkaChipRow[];
  /** The hero's text is centred (`textAlign: Center`): centre the chips too. */
  isCenter: boolean;
  className?: string;
};

/** Labelled rows of attribute values as chips, under the STK hero text. */
export const FlexibleHeroBlockStkBkaAttributeChips: FC<
  FlexibleHeroBlockStkBkaAttributeChipsProps
> = ({ rows, isCenter, className }) => {
  const idPrefix = useId();

  return (
    <div
      className={classNames(
        'flex w-full flex-col gap-spacing/gap/soft-12px',
        className,
      )}
      data-component="flexible-hero-block-stk-bka-attribute-chips"
    >
      {rows.map(({ attributeKey, label, items, chipTextClassName }) => {
        // The visible label names the list, so it is announced once.
        const labelId = `${idPrefix}-${attributeKey}`;

        return (
          <div
            key={attributeKey}
            className="flex flex-col gap-spacing/gap/soft-4px"
          >
            <span
              id={labelId}
              className="mhg-alpha-typography-body-2 text-secondary-lighter"
            >
              {label}:
            </span>
            <ul
              aria-labelledby={labelId}
              className={classNames(
                'flex flex-wrap gap-spacing/gap/soft-4px',
                isCenter && 'justify-center',
              )}
            >
              {/* Values arrive deduplicated (resolveAttributeRow). */}
              {items.map((item) => (
                <li
                  key={item}
                  className={classNames(
                    'mhg-alpha-typography-body-2 rounded-radius/card-inner border border-borders/default px-tag/padding-x py-spacing/padding/soft-6px bg-fill/card',
                    chipTextClassName,
                  )}
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};
